import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './MneaStreamConfigEditor.module.scss';

function MneaStreamConfigEditor({ configData, onConfigChange, isEditingJson }) {
  const [intervalMs, setIntervalMs] = useState(50);
  const [selectedMessages, setSelectedMessages] = useState({
    ALM: false,
    DTM: false,
    GBS: false,
    GGA: false,
    GLL: false,
    GNS: false,
    GRS: false,
    GSA: false,
    GST: false,
    GSV: false,
    HDT: false,
    RMC: false,
    ROT: false,
    VTG: false,
    ZDA: false,
    HRP: false,
    LLQ: false,
    RBP: false,
    RBV: false,
    RBD: false,
    AVR: false,
    GGAaux1: false,
    GGK: false,
    GFA: false,
    GGQ: false,
    LLK: false,
    GMP: false,
    TFM: false,
    SNC: false,
    THS: false,
  });

  const lastConfigDataRef = useRef('');
  const isUpdatingFromGuiRef = useRef(false);

  // Generate JSON from GUI settings
  const generateConfig = useCallback(() => {
    const enabledMessages = Object.keys(selectedMessages)
      .filter(key => selectedMessages[key])
      .join('+');

    const config = {
      messages: enabledMessages,
      interval_ms: parseInt(intervalMs) || 50,
    };

    return JSON.stringify(config, null, 2);
  }, [selectedMessages, intervalMs]);

  // Parse JSON to GUI when configData changes from external source
  useEffect(() => {
    if (
      configData &&
      configData !== lastConfigDataRef.current &&
      !isUpdatingFromGuiRef.current &&
      !isEditingJson
    ) {
      lastConfigDataRef.current = configData;

      try {
        const config = JSON.parse(configData);

        // Parse interval
        setIntervalMs(config.interval_ms || 50);

        // Parse messages
        const newSelectedMessages = { ...selectedMessages };
        // Reset all to false first
        Object.keys(newSelectedMessages).forEach(key => {
          newSelectedMessages[key] = false;
        });

        if (config.messages) {
          const messageList = config.messages.split('+').map(msg => msg.trim());
          messageList.forEach(msg => {
            if (Object.prototype.hasOwnProperty.call(newSelectedMessages, msg)) {
              newSelectedMessages[msg] = true;
            }
          });
        }

        setSelectedMessages(newSelectedMessages);
      } catch (error) {
        console.error('Error parsing NMEA Stream config:', error);
      }
    }
  }, [configData, isEditingJson, selectedMessages]);

  // Update parent component when GUI settings change
  useEffect(() => {
    // Skip the first render and when we're parsing from external config
    if (lastConfigDataRef.current) {
      isUpdatingFromGuiRef.current = true;
      const newConfig = generateConfig();
      lastConfigDataRef.current = newConfig;
      onConfigChange(newConfig);

      // Reset the flag after a short delay to allow the parent to update
      setTimeout(() => {
        isUpdatingFromGuiRef.current = false;
      }, 10);
    }
  }, [selectedMessages, intervalMs, generateConfig, onConfigChange]);

  const handleMessageToggle = messageType => {
    setSelectedMessages(prev => ({
      ...prev,
      [messageType]: !prev[messageType],
    }));
  };

  const handleIntervalChange = e => {
    const value = e.target.value;
    // Only allow positive integers
    if (value === '' || /^\d+$/.test(value)) {
      setIntervalMs(value);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>NMEA Stream Settings</h3>

      {/* Interval setting */}
      <div className={styles.setting}>
        <label className={styles.field}>
          <span className={styles.inputLabel}>Interval (ms)</span>
          <input
            type="text"
            value={intervalMs}
            onChange={handleIntervalChange}
            className={styles.input}
            placeholder="50"
          />
        </label>
      </div>

      {/* Message types */}
      <div className={styles.setting}>
        <div className={styles.label}>Message Types</div>
        <div className={styles.messageGrid}>
          {Object.keys(selectedMessages).map(messageType => (
            <label key={messageType} className={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={selectedMessages[messageType]}
                onChange={() => handleMessageToggle(messageType)}
              />
              {messageType}
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

export default MneaStreamConfigEditor;
